import styles from './PricingTableHeadColumn.scss';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { ContentImage } from 'src/components/ContentImage/ContentImage';
import { useFeatureFlagsContext } from 'src/components/FeatureFlags/FeatureFlags';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { useDesktopOrLarge } from 'src/hooks/UseResponsive';
import { BundlePricings } from 'src/hooks/useDCS';
import { shouldDisplayInstallmentPlans } from 'src/services/dcs/bundle';
import { Bundle, TagFields } from '../types';

type PricingTableHeadColumnProps =
  Pick<Bundle, 'title' | 'description' | 'image' | 'id'>
  & TagFields
  & React.ThHTMLAttributes<HTMLTableCellElement>
  & { colSpanCount?: number; pricings?: BundlePricings };

export const PricingTableHeadColumn: React.FC<PricingTableHeadColumnProps> = ({
  title,
  description,
  image,
  id,
  showTag,
  tagText,
  tagBackgroundColor,
  bundleWithTag,
  colSpanCount,
  pricings,
  ...props
}) => {
  const isDesktopOrLarge = useDesktopOrLarge();
  const { t } = useTranslation();

  const { isFlagEnabled } = useFeatureFlagsContext();
  const isMsrpEnabled = isFlagEnabled('show-msrp-global');
  const isDescriptionEnabled = isFlagEnabled('show-product-description-global');

  const isThisBundleWithTag = bundleWithTag && bundleWithTag.id === id;
  const tagShouldBeShown = isDesktopOrLarge && isThisBundleWithTag && showTag && tagText;

  return (
    <th className={styles.headColumnCell} colSpan={colSpanCount} scope="col" {...props} id={`bundle-${id}`} aria-labelledby={`bundle-text-${id}`}>
      {tagShouldBeShown && (
        <TextV2
          className={styles.headColumnTag}
          style={{ backgroundColor: tagBackgroundColor }}
          data-test="pricing-table-head-column-tag"
          value={tagText}
          variant="eyebrow"
        />
      )}

      <div className={styles.headColumnPriceContainer}>
      <ContentImage entry={image} className={styles.headColumnImage} data-test="image" alt="" />
        <TextV2 className={styles.headColumnTitle} data-test="title" value={title} variant="eyebrow" id={`bundle-text-${id}`} />
        {pricings && (
          <div>
            {isMsrpEnabled && pricings.msrp && (
              <span className={styles.headColumnMsrpContainer}>
                <TextV2
                  value={t('pricing-detail-table.msrp', { price: pricings.msrp.value })}
                  variant="body-small"
                />
              </span>
            )}
            <div className={`${styles.headColumnPrice}  ${!isMsrpEnabled && styles.headColumnPriceMsrpHide}`} data-test="price">
              <TextV2
                value={t('pricing-detail-table.price-info', { basePrice: pricings.basePrice.value })}
                variant="h3-medium"
              />
              {shouldDisplayInstallmentPlans(pricings.basePrice.value) && pricings.installmentPlans.IP3 && (
              <TextV2
                data-test="secondary-price"
                value={t('pricing-detail-table.secondary-price-info', { monthlyInstallmentPrice: pricings.installmentPlans.IP3.value })}
                tagName="small"
                variant="body-medium"
                className={styles.headColumnInstallmentPlan}
              />
            )}
            </div>
            <span className={`${styles.headColumnPrice} ${styles.headColumnMonthlyPrice}`} data-test="monthly-price">
              <TextV2
                value={t('pricing-detail-table.monthly-price', { monthlyPrice: pricings.monthlyAdditionalPrice.value })}
                variant="body-medium"
              />
            </span>
          </div>
        )}
        {isDescriptionEnabled && <TextV2 className={styles.headColumnDescription} data-test="description" value={description} variant="body" tagName="p" />}
      </div>
    </th>
  );
};
